// ============================================
// GARANTIASAVISOSPANEL.JS — "Garantias por vencer" (Entrega 2)
// ============================================
// Un solo componente para dos lugares:
//  - Mi Trabajo (mios=true): los avisos del responsable comercial, con el mensaje
//    "La garantía de X del cliente Y vence el DD/MM — ofrecer servicio o extensión".
//  - Torre de control / pestaña Garantias de Expedientes (mios=false): conteo, lista y filtros.
// Acciones: marcar atendido / descartado (con nota, trazabilidad en backend) y acciones rapidas
// "Crear expediente de servicio" / "Cotizar extension" (pre-llenadas con cliente y productos).
// Sin precios. Responsive: tarjetas apiladas, botones >= 44 px, filtros con wrap.

import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, CheckCircle2, XCircle, Wrench, FilePlus2, RotateCcw } from 'lucide-react';
import { API_BASE_CLI, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE, estiloTarjeta, estiloBotonPrimario, estiloBotonSecundario } from './helpersClientes';

function authHeaders() {
  const t = localStorage.getItem('beep_token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}
const fmtF = (v) => { if (!v) return '—'; const d = new Date(v); return isNaN(d) ? '—' : d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }); };
const FILTROS = [
  { id: 'pendiente', label: 'Pendientes' },
  { id: 'atendido', label: 'Atendidos' },
  { id: 'descartado', label: 'Descartados' },
];

export default function GarantiasAvisosPanel({ mios = false, esCelular = false, compacto = false, onAbrirExpediente, onAbrirCotizacion }) {
  const [estado, setEstado] = useState('pendiente');
  const [tipo, setTipo] = useState(null); // null | por_vencer | vencida
  const [lista, setLista] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [error, setError] = useState(null);
  const [ocupado, setOcupado] = useState(null); // id del aviso en accion
  const [notaDe, setNotaDe] = useState(null);   // { id, estado } pidiendo nota
  const [nota, setNota] = useState('');
  const [toast, setToast] = useState(null);

  const avisar = (t) => { setToast(t); setTimeout(() => setToast(null), 5000); };
  const qsBase = mios ? 'mios=1&' : '';

  const cargar = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API_BASE_CLI}/api/garantias/avisos?${qsBase}estado=${estado}${tipo ? `&tipo=${tipo}` : ''}`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE_CLI}/api/garantias/avisos/resumen?${qsBase}`, { headers: authHeaders() }).then(r => r.json()),
      ]);
      if (!r1.ok) throw new Error(r1.error || 'No se pudieron leer los avisos');
      setLista(r1.avisos || []); setResumen(r2.ok ? r2 : null); setError(null);
    } catch (e) { setError(e.message); setLista([]); }
  }, [qsBase, estado, tipo]);
  useEffect(() => { cargar(); }, [cargar]);

  const marcar = async (id, nuevoEstado, notaTxt) => {
    setOcupado(id);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/garantias/avisos/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ estado: nuevoEstado, nota: notaTxt || null }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudo actualizar');
      setNotaDe(null); setNota('');
      avisar(nuevoEstado === 'atendido' ? 'Aviso marcado como atendido.' : nuevoEstado === 'descartado' ? 'Aviso descartado.' : 'Aviso reabierto.');
      cargar();
    } catch (e) { avisar(e.message); }
    finally { setOcupado(null); }
  };

  const accion = async (a, tipoAccion) => {
    setOcupado(a.id);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/garantias/avisos/${a.id}/accion`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ tipo: tipoAccion }) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409 && j.oportunidad_id) { avisar('Este aviso ya tiene una acción creada; abriendo el expediente.'); if (onAbrirExpediente) onAbrirExpediente(j.oportunidad_id); return; }
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudo crear la acción');
      avisar(tipoAccion === 'cotizar_extension' ? `Expediente ${j.oportunidad.folio} y cotización ${j.cotizacion?.folio || ''} creados (precio por definir).` : `Expediente de servicio ${j.oportunidad.folio} creado.`);
      cargar();
      if (tipoAccion === 'cotizar_extension' && j.cotizacion && onAbrirCotizacion) onAbrirCotizacion(j.cotizacion.id, j.oportunidad.id);
      else if (onAbrirExpediente) onAbrirExpediente(j.oportunidad.id);
    } catch (e) { avisar(e.message); }
    finally { setOcupado(null); }
  };

  const btn = { ...estiloBotonSecundario, minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 };
  const total = resumen ? (resumen.por_vencer || 0) + (resumen.vencidas || 0) : null;

  return (
    <div style={{ ...estiloTarjeta, padding: compacto ? 12 : 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <ShieldAlert size={18} color="#9A5B00" />
        <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_OSCURO }}>Garantías por vencer{total != null ? ` (${total})` : ''}</div>
        {resumen && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginLeft: 'auto' }}>
            {[{ id: null, label: `Todas` }, { id: 'por_vencer', label: `Por vencer · ${resumen.por_vencer || 0}` }, { id: 'vencida', label: `Vencidas · ${resumen.vencidas || 0}` }].map(f => (
              <button key={String(f.id)} type="button" onClick={() => setTipo(f.id)} style={{ ...btn, minHeight: 36, background: tipo === f.id ? COLOR_OSCURO : '#fff', color: tipo === f.id ? '#fff' : COLOR_GRIS }}>{f.label}</button>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {FILTROS.map(f => (
          <button key={f.id} type="button" onClick={() => setEstado(f.id)} style={{ ...btn, minHeight: 36, background: estado === f.id ? '#DEEBF7' : '#fff', color: estado === f.id ? '#265F97' : COLOR_GRIS, fontWeight: 700 }}>{f.label}</button>
        ))}
        {resumen?.job?.ultima_completada_at && <span style={{ fontSize: 11, color: COLOR_GRIS_CLARO, alignSelf: 'center' }}>Última revisión automática: {new Date(resumen.job.ultima_completada_at).toLocaleString('es-MX')}</span>}
      </div>
      {error && <div style={{ fontSize: 12, color: '#dc2626' }}>{error}</div>}
      {!lista && !error && <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Cargando…</div>}
      {lista && lista.length === 0 && <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>{estado === 'pendiente' ? 'Sin garantías por vencer en los próximos 30 días.' : 'Nada en esta categoría.'}</div>}
      <div style={{ display: 'grid', gap: 8 }}>
        {(lista || []).map(a => {
          const vencida = a.tipo === 'vencida';
          const dias = Number(a.dias_restantes);
          return (
            <div key={a.id} style={{ border: `1px solid ${COLOR_BORDE}`, borderLeft: `4px solid ${vencida ? '#dc2626' : '#f59e0b'}`, borderRadius: 10, padding: 12, background: '#fff' }}>
              <div style={{ fontSize: 13, color: COLOR_OSCURO, lineHeight: 1.5 }}>
                La garantía de <strong>{a.producto_nombre}</strong>{a.modelo ? ` (${a.modelo})` : ''} del cliente <strong>{a.cliente_nombre || '—'}</strong>{a.sucursal ? ` · ${a.sucursal}` : ''} {vencida ? <span style={{ color: '#dc2626', fontWeight: 700 }}>venció el {fmtF(a.fecha_vencimiento)}</span> : <span style={{ color: '#9A5B00', fontWeight: 700 }}>vence el {fmtF(a.fecha_vencimiento)}{Number.isFinite(dias) ? ` (en ${dias} día${dias === 1 ? '' : 's'})` : ''}</span>} — ofrecer servicio o extensión.
              </div>
              <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4, display: 'flex', gap: '2px 12px', flexWrap: 'wrap' }}>
                <span>Certificado {a.certificado_folio}</span>
                {a.expediente_folio && <span>Expediente {a.expediente_folio}</span>}
                {!mios && a.responsable_nombre && <span>Responsable: {a.responsable_nombre}</span>}
                {a.estado !== 'pendiente' && <span>{a.estado === 'atendido' ? 'Atendido' : 'Descartado'}{a.atendido_por_nombre ? ` por ${a.atendido_por_nombre}` : ''}{a.atendido_at ? ` · ${new Date(a.atendido_at).toLocaleDateString('es-MX')}` : ''}{a.nota ? ` · ${a.nota}` : ''}</span>}
              </div>
              {a.estado === 'pendiente' ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  <button type="button" disabled={ocupado === a.id} style={{ ...estiloBotonPrimario, minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }} onClick={() => accion(a, 'expediente_servicio')}><Wrench size={15} /> Crear expediente de servicio</button>
                  <button type="button" disabled={ocupado === a.id} style={btn} onClick={() => accion(a, 'cotizar_extension')}><FilePlus2 size={15} /> Cotizar extensión de garantía</button>
                  <button type="button" disabled={ocupado === a.id} style={{ ...btn, color: '#16a34a' }} onClick={() => setNotaDe({ id: a.id, estado: 'atendido' })}><CheckCircle2 size={15} /> Atendido</button>
                  <button type="button" disabled={ocupado === a.id} style={{ ...btn, color: '#dc2626' }} onClick={() => setNotaDe({ id: a.id, estado: 'descartado' })}><XCircle size={15} /> Descartar</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  {a.accion_oportunidad_id && onAbrirExpediente && <button type="button" style={btn} onClick={() => onAbrirExpediente(a.accion_oportunidad_id)}>Abrir expediente creado</button>}
                  <button type="button" disabled={ocupado === a.id} style={btn} onClick={() => marcar(a.id, 'pendiente', null)}><RotateCcw size={15} /> Reabrir</button>
                </div>
              )}
              {notaDe && notaDe.id === a.id && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input style={{ flex: '1 1 220px', minHeight: 44, fontSize: 16, padding: '8px 10px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 10 }} placeholder={notaDe.estado === 'atendido' ? 'Qué se hizo (opcional)' : 'Motivo (opcional)'} value={nota} onChange={e => setNota(e.target.value)} />
                  <button type="button" style={{ ...estiloBotonPrimario, minHeight: 44 }} onClick={() => marcar(a.id, notaDe.estado, nota)}>Confirmar</button>
                  <button type="button" style={btn} onClick={() => { setNotaDe(null); setNota(''); }}>Cancelar</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {toast && <div style={{ marginTop: 10, fontSize: 12, color: COLOR_GRIS, fontWeight: 600 }}>{toast}</div>}
    </div>
  );
}
